import React, { useState, useEffect, useMemo } from 'react';
import {
  RefreshCw, TrendingUp, TrendingDown, AlertCircle, ChevronUp, ChevronDown,
  Pencil, Trash2, Plus, Check, X,
} from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { apiCall, default as API_CONFIG } from '../config/api';

/**
 * Unified portfolio view across Schwab Joint, Schwab Individual, Schwab IRA,
 * and Crypto.
 *
 * The two backend endpoints stay separate (Dashboard depends on /portfolio
 * being stocks-only). We pull both in parallel and merge into one list,
 * normalizing the crypto rows into the same shape as the stock rows
 * (averageCost, marketValue, unrealizedGainLoss, etc.). Each row carries
 * a portfolioType so the account-filter pills can slice the list.
 *
 * Symbols are clickable -- they navigate to the ticker detail page.
 */
const PORTFOLIO_FILTERS = [
  { id: 'all',               label: 'All' },
  { id: 'schwab-joint',      label: 'Joint' },
  { id: 'schwab-individual', label: 'Individual' },
  { id: 'schwab-ira',        label: 'IRA' },
  { id: 'crypto',            label: 'Crypto' },
];

/**
 * Full-width editing row for a crypto holding.
 *
 * Spans the table rather than turning individual cells into inputs: the row
 * has ten columns, most of them derived (market value, gain/loss, %), and only
 * two are actually editable. A spanning form makes it obvious that quantity
 * and cost basis are the inputs and everything else recalculates.
 *
 * Cost basis is the TOTAL position cost, matching the API and what a
 * bitcoin.tax export gives you -- not the per-unit average the table displays.
 */
const CryptoEditRow = ({ draft, onChange, onSave, onCancel, saving, symbolEditable = false }) => {
  const submitOnEnter = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); onSave(); }
    if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
  };

  return (
    <tr className="crypto-edit-row">
      <td colSpan={10}>
        <div className="crypto-edit-form">
          <label>
            <span>Symbol</span>
            <input
              type="text"
              value={draft.symbol || ''}
              onChange={(e) => onChange({ ...draft, symbol: e.target.value })}
              onKeyDown={submitOnEnter}
              disabled={!symbolEditable || saving}
              placeholder="BTC"
              autoFocus={symbolEditable}
              style={{ textTransform: 'uppercase' }}
            />
          </label>

          <label>
            <span>Quantity</span>
            <input
              type="number"
              step="any"
              min="0"
              value={draft.quantity}
              onChange={(e) => onChange({ ...draft, quantity: e.target.value })}
              onKeyDown={submitOnEnter}
              disabled={saving}
              placeholder="0.00"
              autoFocus={!symbolEditable}
            />
          </label>

          <label>
            <span>Total cost basis ($)</span>
            <input
              type="number"
              step="any"
              min="0"
              value={draft.costBasis}
              onChange={(e) => onChange({ ...draft, costBasis: e.target.value })}
              onKeyDown={submitOnEnter}
              disabled={saving}
              placeholder="0.00"
            />
          </label>

          <div className="crypto-edit-actions">
            <button type="button" className="btn btn-primary btn-tiny" onClick={onSave} disabled={saving}>
              <Check size={14} />
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="btn btn-secondary btn-tiny" onClick={onCancel} disabled={saving}>
              <X size={14} />
              Cancel
            </button>
          </div>
        </div>
      </td>
    </tr>
  );
};

const Portfolio = () => {
  const navigate = useNavigate();
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');

  // Crypto editing state. Crypto has no broker feed, so add/edit/delete here
  // is the only way the position list stays honest as coins are moved, lost,
  // or dusted.
  const [dustThreshold, setDustThreshold] = useState(100);
  const [editingSymbol, setEditingSymbol] = useState(null);
  const [editDraft, setEditDraft] = useState({ quantity: '', costBasis: '' });
  const [adding, setAdding] = useState(false);
  const [addDraft, setAddDraft] = useState({ symbol: '', quantity: '', costBasis: '' });
  const [savingSymbol, setSavingSymbol] = useState(null);
  const [cryptoError, setCryptoError] = useState(null);
  const [cryptoNotice, setCryptoNotice] = useState(null);

  const isCryptoView = activeFilter === 'crypto';

  const beginEdit = (holding) => {
    setCryptoError(null);
    setCryptoNotice(null);
    setAdding(false);
    setEditingSymbol(holding.symbol);
    setEditDraft({
      quantity: String(holding.quantity ?? ''),
      costBasis: String(holding.costBasis ?? ''),
    });
  };

  const cancelEdit = () => {
    setEditingSymbol(null);
    setEditDraft({ quantity: '', costBasis: '' });
  };

  const saveHolding = async (symbol, draft) => {
    const quantity = Number(draft.quantity);
    const costBasis = Number(draft.costBasis);

    if (!symbol || !symbol.trim()) {
      setCryptoError('Symbol is required.');
      return;
    }
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setCryptoError('Quantity must be a number greater than zero. To remove a holding, delete it.');
      return;
    }
    if (!Number.isFinite(costBasis) || costBasis < 0) {
      setCryptoError('Cost basis must be zero or greater.');
      return;
    }

    setSavingSymbol(symbol);
    setCryptoError(null);
    setCryptoNotice(null);

    try {
      const result = await apiCall(API_CONFIG.endpoints.cryptoHoldings, {
        method: 'POST',
        body: JSON.stringify({ symbol: symbol.trim().toUpperCase(), quantity, costBasis }),
      });

      // The backend saves even when CoinGecko has no price for the symbol --
      // surface that rather than letting a $0 market value read as "worthless".
      if (result && result.priceResolved === false) {
        setCryptoNotice(result.note || 'Saved, but no current price was available for this symbol.');
      }

      cancelEdit();
      setAdding(false);
      setAddDraft({ symbol: '', quantity: '', costBasis: '' });
      await fetchPortfolio();
    } catch (err) {
      setCryptoError(err.message || 'Failed to save the holding.');
    } finally {
      setSavingSymbol(null);
    }
  };

  const deleteHolding = async (symbol) => {
    // Hard delete with no undo -- confirm before destroying a cost-basis record.
    if (!window.confirm(
      `Delete ${symbol} from your crypto portfolio?\n\nThis removes the holding and its cost basis. It cannot be undone.`
    )) {
      return;
    }

    setSavingSymbol(symbol);
    setCryptoError(null);
    setCryptoNotice(null);

    try {
      await apiCall(API_CONFIG.endpoints.cryptoHoldingBySymbol(symbol), { method: 'DELETE' });
      await fetchPortfolio();
    } catch (err) {
      setCryptoError(err.message || `Failed to delete ${symbol}.`);
    } finally {
      setSavingSymbol(null);
    }
  };

  // Default sort = symbol asc (alphabetical). Click a header to switch
  // column; click the same header to flip direction.
  const [sortKey, setSortKey] = useState('symbol');
  const [sortDir, setSortDir] = useState('asc');

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Numeric columns default to desc on first click; symbol/name/account default asc.
      const stringCols = new Set(['symbol', 'name', 'portfolioType']);
      setSortDir(stringCols.has(key) ? 'asc' : 'desc');
    }
  };

  useEffect(() => {
    fetchPortfolio();
  }, []);

  const fetchPortfolio = async () => {
    try {
      setLoading(true);
      setError(null);

      // Schwab + crypto in parallel. Either can fail independently --
      // the crypto endpoint 404s when no crypto portfolio has been
      // uploaded yet, which shouldn't blank the stock list.
      // Crypto comes from the management endpoint rather than /crypto/portfolio
      // so dust positions are available to edit. They're still hidden from the
      // combined views -- see `filtered` -- but they have to be *loaded* to be
      // deletable, and hiding them at the API was what made cleanup impossible.
      const [stockData, cryptoData] = await Promise.all([
        apiCall(API_CONFIG.endpoints.portfolio).catch(() => ({ holdings: [] })),
        apiCall(API_CONFIG.endpoints.cryptoHoldings).catch(() => ({ holdings: [] })),
      ]);

      const stockRows = (stockData.holdings || []).map((h) => ({
        ...h,
        portfolioType: h.portfolioType || 'schwab-joint',
        source: h.source || 'Schwab',
      }));

      const threshold = cryptoData.dustThreshold ?? 100;
      setDustThreshold(threshold);

      // Crypto's wire shape is leaner -- normalize so the table renders the
      // same columns. costBasis is the TOTAL position cost, not per-unit.
      const cryptoRows = (cryptoData.holdings || []).map((h) => {
        const qty = h.quantity || 0;
        const costBasis = h.costBasis || 0;
        const marketValue = h.currentValue || 0;
        const unrealizedGainLoss = marketValue - costBasis;
        const unrealizedGainLossPercent = costBasis > 0
          ? (unrealizedGainLoss / costBasis) * 100
          : 0;
        return {
          symbol: h.symbol,
          name: h.name || h.symbol,
          assetType: 'CRYPTO',
          quantity: qty,
          costBasis,
          averageCost: qty > 0 ? costBasis / qty : 0,
          currentPrice: h.currentPrice || 0,
          marketValue,
          unrealizedGainLoss,
          unrealizedGainLossPercent,
          source: 'Crypto',
          portfolioType: 'crypto',
          isDust: marketValue < threshold,
        };
      });

      const merged = [...stockRows, ...cryptoRows];
      setHoldings(merged);

      // Last-sync = whichever side reported the freshest timestamp.
      const stockSync = stockData.lastSync ? new Date(stockData.lastSync) : null;
      const cryptoSync = cryptoData.lastUpdated ? new Date(cryptoData.lastUpdated) : null;
      const freshest = [stockSync, cryptoSync].filter(Boolean).sort((a, b) => b - a)[0];
      setLastSync(freshest || new Date());

      setLoading(false);
    } catch (err) {
      setError(err.message || 'Failed to load portfolio data');
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setSyncing(true);
    await fetchPortfolio();
    setSyncing(false);
  };

  // Apply filter; counts shown alongside each pill use the unfiltered list.
  //
  // Dust rule: sub-threshold crypto stays hidden in the combined views (a $5
  // token is noise next to real positions) but is fully visible under the
  // Crypto filter, which doubles as the management view. That's the only place
  // it can be cleaned up, so it can't be hidden there.
  const filtered = useMemo(() => {
    const base = activeFilter === 'all'
      ? holdings.filter((h) => !h.isDust)
      : holdings.filter((h) => h.portfolioType === activeFilter);

    // Sort copy so React's referential checks see new array each click.
    return [...base].sort((a, b) => compareRows(a, b, sortKey, sortDir));
  }, [holdings, activeFilter, sortKey, sortDir]);

  const counts = useMemo(() => {
    // Each pill counts what clicking it would actually show, so the number
    // never disagrees with the table underneath it.
    const byType = holdings.reduce((acc, h) => {
      acc[h.portfolioType] = (acc[h.portfolioType] || 0) + 1;
      return acc;
    }, {});
    return { ...byType, all: holdings.filter((h) => !h.isDust).length };
  }, [holdings]);

  const dustCount = useMemo(() => holdings.filter((h) => h.isDust).length, [holdings]);

  const totals = useMemo(() => {
    return filtered.reduce(
      (acc, holding) => ({
        marketValue: acc.marketValue + (holding.marketValue || 0),
        gainLoss: acc.gainLoss + (holding.unrealizedGainLoss || 0),
      }),
      { marketValue: 0, gainLoss: 0 }
    );
  }, [filtered]);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error">
        <AlertCircle size={24} />
        <p>{error}</p>
      </div>
    );
  }

  const totalGainLossPercent = totals.marketValue - totals.gainLoss > 0
    ? (totals.gainLoss / (totals.marketValue - totals.gainLoss)) * 100
    : 0;

  const goToTicker = (symbol, name) => {
    if (!symbol) return;
    // fromPortfolio turns a 404 into the "link this holding" flow rather than
    // a dead end -- cross-listed names (a Lanxess ADR held as LNXSY while Russ
    // posts about LXS) land there by default until someone curates the alias.
    navigate(`/mentions/tickers/${encodeURIComponent(symbol)}`, {
      state: { fromPortfolio: true, name },
    });
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Portfolio Holdings</h1>
          <p>Combined view across Schwab Joint, Individual, IRA, and crypto.</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-label">Total Market Value</div>
          <div className="stat-value">${totals.marketValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Total Gain/Loss</div>
          <div className="stat-value" style={{ color: totals.gainLoss >= 0 ? 'var(--success-color)' : 'var(--danger-color)' }}>
            ${Math.abs(totals.gainLoss).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className={`stat-change ${totals.gainLoss >= 0 ? 'positive' : 'negative'}`}>
            {totals.gainLoss >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
            {totalGainLossPercent.toFixed(2)}%
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Last Sync</div>
          <div className="stat-value" style={{ fontSize: '1.25rem' }}>
            {lastSync ? format(lastSync, 'h:mm a') : 'Never'}
          </div>
          <button
            className="btn btn-primary"
            onClick={handleRefresh}
            disabled={syncing}
            style={{ marginTop: '0.5rem', fontSize: '0.875rem', padding: '0.5rem 1rem' }}
          >
            <RefreshCw size={14} className={syncing ? 'spinning' : ''} />
            {syncing ? 'Syncing...' : 'Sync Now'}
          </button>
        </div>
      </div>

      {/* Account filter */}
      <div className="filter-buttons" style={{ marginBottom: '1rem' }}>
        {PORTFOLIO_FILTERS.map((f) => {
          const count = counts[f.id] || 0;
          const isActive = activeFilter === f.id;
          // Hide pills with zero entries (except "All") so users don't see
          // a phantom "Crypto" filter before any crypto portfolio exists.
          if (f.id !== 'all' && count === 0) return null;
          return (
            <button
              key={f.id}
              className={`btn ${isActive ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setActiveFilter(f.id)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}
            >
              {f.label}
              <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>({count})</span>
            </button>
          );
        })}
      </div>

      {/* Holdings Table - Desktop */}
      <div className="card holdings-table-desktop">
        <div className="card-header">
          <h2>Holdings</h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <span className="badge badge-info">{filtered.length} positions</span>
          </div>
        </div>

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <SortableHeader label="Symbol"        sortKey="symbol"                    activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortableHeader label="Name"          sortKey="name"                      activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                <SortableHeader label="Quantity"      sortKey="quantity"                  activeKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" />
                <SortableHeader label="Avg Cost"      sortKey="averageCost"               activeKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" />
                <SortableHeader label="Current Price" sortKey="currentPrice"              activeKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" />
                <SortableHeader label="Market Value"  sortKey="marketValue"               activeKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" />
                <SortableHeader label="Gain/Loss"     sortKey="unrealizedGainLoss"        activeKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" />
                <SortableHeader label="%"             sortKey="unrealizedGainLossPercent" activeKey={sortKey} dir={sortDir} onClick={toggleSort} align="right" />
                <SortableHeader label="Account"       sortKey="portfolioType"             activeKey={sortKey} dir={sortDir} onClick={toggleSort} />
                {isCryptoView && <th style={{ width: '5rem' }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {isCryptoView && adding && (
                <CryptoEditRow
                  draft={addDraft}
                  onChange={setAddDraft}
                  onSave={() => saveHolding(addDraft.symbol, addDraft)}
                  onCancel={() => { setAdding(false); setCryptoError(null); }}
                  saving={savingSymbol !== null}
                  symbolEditable
                />
              )}

              {filtered.map((holding, index) => (
                editingSymbol === holding.symbol ? (
                  <CryptoEditRow
                    key={`edit-${holding.symbol}`}
                    draft={{ ...editDraft, symbol: holding.symbol }}
                    onChange={(d) => setEditDraft(d)}
                    onSave={() => saveHolding(holding.symbol, editDraft)}
                    onCancel={cancelEdit}
                    saving={savingSymbol === holding.symbol}
                  />
                ) : (
                <tr key={`${holding.portfolioType}-${holding.symbol}-${index}`}>
                  <td>
                    <button
                      type="button"
                      onClick={() => goToTicker(holding.symbol, holding.name)}
                      className="symbol-link"
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        font: 'inherit',
                        color: 'var(--primary-color)',
                        fontWeight: 700,
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        textDecorationColor: 'transparent',
                        transition: 'text-decoration-color 120ms',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.textDecorationColor = 'currentColor'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.textDecorationColor = 'transparent'; }}
                    >
                      {holding.symbol || 'N/A'}
                    </button>
                  </td>
                  <td>{holding.name || holding.symbol || 'N/A'}</td>
                  <td>{(holding.quantity || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</td>
                  <td>${(holding.averageCost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td>${(holding.currentPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td>
                    <strong>${(holding.marketValue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                  </td>
                  <td style={{ color: (holding.unrealizedGainLoss || 0) >= 0 ? 'var(--success-color)' : 'var(--danger-color)' }}>
                    {(holding.unrealizedGainLoss || 0) >= 0 ? '+' : ''}${(holding.unrealizedGainLoss || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td style={{ color: (holding.unrealizedGainLossPercent || 0) >= 0 ? 'var(--success-color)' : 'var(--danger-color)' }}>
                    {(holding.unrealizedGainLossPercent || 0) >= 0 ? '+' : ''}{(holding.unrealizedGainLossPercent || 0).toFixed(2)}%
                  </td>
                  <td>
                    <AccountBadge portfolioType={holding.portfolioType} />
                    {holding.isDust && (
                      <span
                        className="badge badge-warning dust-badge"
                        title={`Under the $${dustThreshold} dust threshold, so it's hidden from the combined view`}
                      >
                        dust
                      </span>
                    )}
                  </td>
                  {isCryptoView && (
                    <td>
                      <div className="crypto-row-actions">
                        <button
                          type="button"
                          className="btn btn-secondary btn-tiny"
                          onClick={() => beginEdit(holding)}
                          disabled={savingSymbol === holding.symbol}
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary btn-tiny btn-danger-ghost"
                          onClick={() => deleteHolding(holding.symbol)}
                          disabled={savingSymbol === holding.symbol}
                          title={`Delete ${holding.symbol}`}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
                )
              ))}
            </tbody>
          </table>
        </div>

        {filtered.length === 0 && (
          <div className="empty-state">
            <p>No holdings found{activeFilter !== 'all' ? ` for ${labelForFilter(activeFilter)}` : ''}</p>
          </div>
        )}

        {isCryptoView && (
          <div className="crypto-manage-bar">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setAdding(true);
                cancelEdit();
                setCryptoError(null);
                setCryptoNotice(null);
              }}
              disabled={adding}
            >
              <Plus size={15} />
              Add holding
            </button>

            <span className="crypto-manage-hint">
              Crypto is maintained by hand -- there's no broker feed.
              {dustCount > 0 && ` ${dustCount} position${dustCount === 1 ? '' : 's'} under $${dustThreshold} `}
              {dustCount > 0 && `${dustCount === 1 ? 'is' : 'are'} shown here but hidden from the combined view.`}
            </span>
          </div>
        )}

        {isCryptoView && cryptoError && (
          <div className="crypto-manage-message error">
            <AlertCircle size={16} />
            {cryptoError}
          </div>
        )}

        {isCryptoView && cryptoNotice && (
          <div className="crypto-manage-message notice">
            <AlertCircle size={16} />
            {cryptoNotice}
          </div>
        )}
      </div>

      {/* Holdings Cards - Mobile */}
      <div className="holdings-cards-mobile">
        <div className="card-header" style={{ marginBottom: '1rem' }}>
          <h2>Holdings</h2>
          <span className="badge badge-info">{filtered.length} positions</span>
        </div>

        {filtered.map((holding, index) => (
          <div key={`${holding.portfolioType}-${holding.symbol}-${index}`} className="card holding-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
              <div>
                <button
                  type="button"
                  onClick={() => goToTicker(holding.symbol, holding.name)}
                  style={{
                    background: 'none', border: 'none', padding: 0, font: 'inherit',
                    color: 'var(--primary-color)', fontWeight: 700, fontSize: '1.125rem',
                    cursor: 'pointer', textDecoration: 'underline', textDecorationColor: 'transparent',
                  }}
                >
                  {holding.symbol || 'N/A'}
                </button>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0.25rem 0 0' }}>
                  {holding.name || holding.symbol || 'N/A'}
                </p>
              </div>
              <AccountBadge portfolioType={holding.portfolioType} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', fontSize: '0.875rem' }}>
              <div>
                <span style={{ color: 'var(--text-secondary)', display: 'block', fontSize: '0.75rem' }}>Market Value</span>
                <strong>${(holding.marketValue || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ color: 'var(--text-secondary)', display: 'block', fontSize: '0.75rem' }}>Gain/Loss</span>
                <strong style={{ color: (holding.unrealizedGainLoss || 0) >= 0 ? 'var(--success-color)' : 'var(--danger-color)' }}>
                  {(holding.unrealizedGainLoss || 0) >= 0 ? '+' : ''}${(holding.unrealizedGainLoss || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  <span style={{ marginLeft: '0.25rem' }}>
                    ({(holding.unrealizedGainLossPercent || 0) >= 0 ? '+' : ''}{(holding.unrealizedGainLossPercent || 0).toFixed(2)}%)
                  </span>
                </strong>
              </div>
              <div>
                <span style={{ color: 'var(--text-secondary)', display: 'block', fontSize: '0.75rem' }}>Quantity</span>
                <span>{(holding.quantity || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}</span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ color: 'var(--text-secondary)', display: 'block', fontSize: '0.75rem' }}>Current Price</span>
                <span>${(holding.currentPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>
        ))}

        {filtered.length === 0 && (
          <div className="empty-state">
            <p>No holdings found{activeFilter !== 'all' ? ` for ${labelForFilter(activeFilter)}` : ''}</p>
          </div>
        )}
      </div>
    </div>
  );
};

const AccountBadge = ({ portfolioType }) => {
  const variant =
    portfolioType === 'crypto' ? 'warning'
    : portfolioType === 'schwab-ira' ? 'success'
    : portfolioType === 'schwab-individual' ? 'accent'
    : 'info';
  return <span className={`badge badge-${variant}`}>{labelForFilter(portfolioType)}</span>;
};

const labelForFilter = (id) => {
  const f = PORTFOLIO_FILTERS.find((x) => x.id === id);
  return f ? f.label : id;
};

/**
 * Click-to-sort table header. Active column shows a chevron in the
 * current direction; inactive columns render a faint placeholder so the
 * column widths don't jump when the user switches columns.
 */
const SortableHeader = ({ label, sortKey, activeKey, dir, onClick, align = 'left' }) => {
  const isActive = activeKey === sortKey;
  const Icon = isActive ? (dir === 'asc' ? ChevronUp : ChevronDown) : ChevronDown;
  return (
    <th
      style={{
        cursor: 'pointer',
        userSelect: 'none',
        textAlign: align,
        whiteSpace: 'nowrap',
      }}
      onClick={() => onClick(sortKey)}
    >
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.25rem',
        flexDirection: align === 'right' ? 'row-reverse' : 'row',
      }}>
        <span>{label}</span>
        <Icon size={12} style={{ opacity: isActive ? 1 : 0.25 }} />
      </span>
    </th>
  );
};

/**
 * Compare two holdings by the chosen key. Numeric fields use numeric
 * compare; string fields use locale-aware compare (so AAPL < BTC etc.).
 * Null/undefined values sort to the end regardless of direction so empty
 * cells don't dominate the top of the table.
 */
const compareRows = (a, b, key, dir) => {
  const av = a?.[key];
  const bv = b?.[key];

  // Push null/undefined to the bottom in BOTH directions.
  const aNull = av == null;
  const bNull = bv == null;
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;

  const stringCols = new Set(['symbol', 'name', 'portfolioType']);
  let cmp;
  if (stringCols.has(key)) {
    cmp = String(av).localeCompare(String(bv));
  } else {
    cmp = (Number(av) || 0) - (Number(bv) || 0);
  }
  return dir === 'asc' ? cmp : -cmp;
};

export default Portfolio;
